import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, FileEdit, Trash2, Filter, Grid, List, Clock, DollarSign, MessageCircle, Plus } from "lucide-react";
import NavBar from "../components/TopNav";
import { toast } from "sonner";
import { authenticatedGet, authenticatedDelete } from "../utils/apiUtils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Will {
  id: string;
  title: string;
  description: string;
  budget: number;
  currency: string;
  fulfillmentTime: string;
  category: string;
  location: string;
  wanterId: string;
  wanterName: string;
  createdAt: string;
  status: string;
  media?: {
    id?: string;
    name?: string;
    mimeType?: string;
    url: string;
    type?: string;
  }[];
}

const mapStatusToUrgency = (status: string, fulfillmentTime?: string): "low" | "medium" | "high" => {
  if (!fulfillmentTime) return "low";
  const dueDate = new Date(fulfillmentTime);
  const today = new Date();
  const oneWeek = new Date();
  oneWeek.setDate(today.getDate() + 7);
  return dueDate <= oneWeek ? "high" : "medium";
};

const currencySymbols: { [key: string]: string } = {
  USD: '$', EUR: '€', ILS: '₪', GBP: '£'
};

const getCurrencySymbol = (currency: string | null): string => {
  if (!currency) return '₪';
  return currencySymbols[currency] || '₪';
};

export const MyWills = () => {
  const [wills, setWills] = useState<Will[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("newest");
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [willToDelete, setWillToDelete] = useState<string | null>(null);
  
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();

  useEffect(() => {
    const loadWills = async () => {
      try {
        setLoading(true);
        const response = await authenticatedGet(`${import.meta.env.VITE_API_URL}/wills/user/${userId}`);
        
        if (!response.ok) {
          throw new Error('Failed to load wills');
        }
        
        const data = await response.json();
        console.log('Wills response:', data);
        setWills(data.content);
      } catch (error) {
        console.error('Failed to load wills:', error);
        toast.error("Failed to load your wills");
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      loadWills();
    } else {
      navigate('/signin');
    }
  }, [userId, navigate]);

  const handleDeleteWill = async (willId: string) => {
    try {
      const response = await authenticatedDelete(`${import.meta.env.VITE_API_URL}/wills/${willId}`);
      
      if (!response.ok) {
        throw new Error('Failed to delete will');
      }
      
      setWills(wills.filter(will => will.id !== willId));
      toast.success("Will deleted successfully");
    } catch (error) {
      console.error('Failed to delete will:', error);
      toast.error("Failed to delete will");
    } finally {
      setWillToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const confirmDelete = (willId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWillToDelete(willId);
    setDeleteDialogOpen(true);
  };

  const handleEditWill = (willId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const willToEdit = wills.find(will => will.id === willId);
    if (willToEdit) {
      navigate(`/will/${willId}/edit`, {
        state: { 
          will: {
            id: willToEdit.id,
            title: willToEdit.title,
            description: willToEdit.description,
            budget: willToEdit.budget,
            currency: willToEdit.currency,
            fulfillmentTime: willToEdit.fulfillmentTime,
            category: willToEdit.category,
            location: willToEdit.location
          }
        }
      });
    } else {
      navigate(`/will/${willId}/edit`);
    }
  };

  const handleViewWill = (willId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/will/${willId}`);
  };

  const handleCreateNewWill = () => {
    navigate('/create-will');
  };

  const getFilteredWills = () => {
    let filtered = statusFilter === "all" 
      ? wills 
      : wills.filter(will => will.status === statusFilter);

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "newest": return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case "oldest": return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case "budget-high": return b.budget - a.budget;
        case "budget-low": return a.budget - b.budget;
        case "due-soon": 
          const dateA = a.fulfillmentTime ? new Date(a.fulfillmentTime).getTime() : Infinity;
          const dateB = b.fulfillmentTime ? new Date(b.fulfillmentTime).getTime() : Infinity;
          return dateA - dateB;
        default: return 0;
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case "ACTIVE": return "bg-green-100 text-green-800 border-green-300";
      case "FULFILLED": return "bg-blue-100 text-blue-800 border-blue-300";
      case "PENDING": return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "CANCELLED": return "bg-red-100 text-red-800 border-red-300";
      default: return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const handleWillClick = (will) => {
    navigate(`/will/${will.id}`, { 
      state: { 
        will: {
          ...will,
          media: will.media?.map(item => ({
            ...item,
            url: item.url.startsWith('http') ? item.url : `${import.meta.env.VITE_API_URL}${item.url}`,
            type: item.type || (
              item.mimeType?.startsWith('image/') ? 'image' : 
              item.mimeType?.startsWith('video/') ? 'video' : 'document'
            ),
            id: item.id || `media-${Math.random()}`,
            name: item.name || 'File',
            mimeType: item.mimeType || 'application/octet-stream'
          }))
        } 
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <main className="container mx-auto px-4 pt-24 pb-12 md:pt-28">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-0">My Wants</h1>
            
            <div className="flex w-full sm:w-auto flex-col sm:flex-row gap-2 sm:gap-3">
              
              
              <div className="flex gap-2 sm:gap-0">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  className={`${viewMode === 'grid' ? 'border-gray-900 bg-gray-100' : ''} hover:bg-gray-900 hover:text-white`}
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline" 
                  size="icon"
                  onClick={() => setViewMode('list')}
                  className={`${viewMode === 'list' ? 'border-gray-900 bg-gray-100' : ''} hover:bg-gray-900 hover:text-white`}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-8">
            <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-2 sm:gap-4">
              <Select 
                value={statusFilter} 
                onValueChange={setStatusFilter}
              >
                <SelectTrigger className="w-full sm:w-[180px] bg-white data-[state=open]:bg-gray-900 data-[state=open]:text-white">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">All Wants</SelectItem>
                  <SelectItem value="ACTIVE" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Active</SelectItem>
                  <SelectItem value="FULFILLED" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Fulfilled</SelectItem>
                  <SelectItem value="PENDING" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Pending</SelectItem>
                  <SelectItem value="CANCELLED" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <Select 
                value={sortBy} 
                onValueChange={setSortBy}
              >
                <SelectTrigger className="w-full sm:w-[180px] bg-white data-[state=open]:bg-gray-900 data-[state=open]:text-white">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="newest" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Newest First</SelectItem>
                  <SelectItem value="oldest" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Oldest First</SelectItem>
                  <SelectItem value="budget-high" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Budget: High to Low</SelectItem>
                  <SelectItem value="budget-low" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Budget: Low to High</SelectItem>
                  <SelectItem value="due-soon" className="hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white">Due Date: Soonest</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              </div>
            ) : getFilteredWills().length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <MessageCircle className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No wants spotted yet</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {statusFilter === "all" 
                    ? "You haven't created any wants yet. Get started by creating your first want."
                    : `No ${statusFilter.toLowerCase()} wants found.`}
                </p>
                <Button 
                  onClick={handleCreateNewWill}
                  className="mt-6 bg-gray-900 hover:bg-gray-800 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  What do you want?
                </Button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-4">
                {getFilteredWills().map((will) => (
                  <div 
                    key={will.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleWillClick(will)}
                  >
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(will.status)}`}>
                          {will.status}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(will.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">{will.title}</h3>
                      
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">{will.description}</p>
                      
                      <div className="flex items-center gap-3 text-gray-500 text-sm mb-4">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {will.fulfillmentTime 
                            ? new Date(will.fulfillmentTime).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })
                            : 'No deadline'
                          }
                        </div>
                        
                        {will.budget > 0 && (
                          <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                            {getCurrencySymbol(will.currency)}{will.budget}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-between pt-3 border-t border-gray-100">
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            onClick={(e) => handleViewWill(will.id, e)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 px-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                            onClick={(e) => handleEditWill(will.id, e)}
                          >
                            <FileEdit className="h-3.5 w-3.5 mr-1" />
                            Edit
                          </Button>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 px-2 text-red-600 hover:text-red-800 hover:bg-red-50"
                          onClick={(e) => confirmDelete(will.id, e)}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {getFilteredWills().map((will) => (
                  <div 
                    key={will.id}
                    className="p-4 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleWillClick(will)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                      <div className="flex items-center gap-2 sm:gap-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(will.status)}`}>
                          {will.status}
                        </span>
                        <span className="text-xs text-gray-500">
                          Created: {new Date(will.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="h-4 w-4" />
                          {will.fulfillmentTime 
                            ? `Due: ${new Date(will.fulfillmentTime).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}`
                            : 'No deadline'
                          }
                        </div>
                        
                        {will.budget > 0 && (
                          <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                            {getCurrencySymbol(will.currency)}{will.budget}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{will.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{will.description}</p>
                    
                    <div className="flex flex-wrap justify-between items-center mt-4 gap-2">
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8 text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-700"
                          onClick={(e) => handleEditWill(will.id, e)}
                        >
                          <FileEdit className="h-3.5 w-3.5 mr-1.5" />
                          Edit Want
                        </Button>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        onClick={(e) => confirmDelete(will.id, e)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-[95%] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will permanently delete this will. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <AlertDialogCancel className="sm:mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => willToDelete && handleDeleteWill(willToDelete)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyWills;
